/**
 * Single emit path for GEICO Tag Manager.
 * Feature code does not call window._gtm.track. This module stamps the visit
 * and delivers the payload. Session fields are added here, not by feature code.
 */

export const GtmMetric = Object.freeze({
  PageView: "PageView",
  Click: "Click",
  ModalLoad: "modalLoad",
});

export const GtmEventType = Object.freeze({
  PageLoad: "PageLoad",
  PageExit: "PageExit",
  ButtonClick: "ButtonClick",
  LinkClick: "LinkClick",
  RadioChange: "RadioChange",
  CheckboxChange: "CheckboxChange",
});

const MAX_ATTEMPTS = 5;
const RETRY_MS = 2000;
const READY_CEILING_MS = 5000;
const SESSION_POLL_MS = 200;
const USER_TYPE = "Independent Agent";

let visit = null;
let sessionReady = false;
let sessionContext = {
  sessionId: "N/A",
  AgentId: "",
  commissionsSOB: "",
};
let lastExitEpoch = 0;
let timers = [];
const previousRadioByName = new Map();

function getTrackFn() {
  const gtm = typeof window !== "undefined" ? window._gtm : undefined;
  return gtm && typeof gtm.track === "function" ? gtm.track.bind(gtm) : null;
}

export function createPageViewId() {
  const cryptoApi =
    (typeof window !== "undefined" && window.crypto) ||
    (typeof crypto !== "undefined" ? crypto : undefined);

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = (Date.now() + i) & 0xff;
    }
  }

  // RFC 4122 version 4 UUID
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  let hex = "";
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, "0");
  }
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function toIsoTimestamp(epochMs) {
  return new Date(epochMs).toISOString();
}

export function beginVisit({ pageName, previousPageName, isSoftNav }) {
  const now = Date.now();
  const t1Epoch = isSoftNav ? Math.max(now, lastExitEpoch) : now;
  visit = {
    pageName,
    pageViewId: createPageViewId(),
    previousPageName: previousPageName || "",
    t1Epoch,
    t1Perf: typeof performance !== "undefined" ? performance.now() : 0,
  };
  return visit;
}

export function getVisit() {
  return visit;
}

export function getVisitElapsed() {
  if (!visit || typeof performance === "undefined") {
    return 0;
  }
  return Math.max(0, Math.round(performance.now() - visit.t1Perf));
}

export function markVisitExit(epochMs) {
  lastExitEpoch = epochMs;
}

export function rememberRadioSelection(groupKey, selectedRadioId) {
  const previouslySelectedRadioId = previousRadioByName.get(groupKey) || "";
  previousRadioByName.set(groupKey, selectedRadioId);
  return previouslySelectedRadioId;
}

export function stampEvent(payload) {
  return stampVisit(payload);
}

function stampVisit(payload) {
  const copy = { ...payload };
  const now = Date.now();
  if (!visit || (copy.pageName && copy.pageName !== visit.pageName)) {
    copy.eventTimestampEpochMs = now;
    copy.eventTimestampUtc = toIsoTimestamp(now);
    delete copy.pageViewId;
    return copy;
  }

  const elapsed = getVisitElapsed();
  const epochMs = visit.t1Epoch + elapsed;
  copy.pageName = copy.pageName || visit.pageName;
  copy.pageViewId = visit.pageViewId;
  copy.eventTimestampEpochMs = epochMs;
  copy.eventTimestampUtc = toIsoTimestamp(epochMs);
  return copy;
}

export function setGtmSession({ sessionId, agentId, agencyId } = {}) {
  sessionContext = {
    sessionId: sessionId || "N/A",
    AgentId: agentId || "",
    commissionsSOB: agencyId || "",
  };
  sessionReady = true;
}

export function markGtmSessionUnavailable() {
  sessionContext = {
    sessionId: "N/A",
    AgentId: "",
    commissionsSOB: "",
  };
  sessionReady = true;
}

function enrich(payload) {
  return {
    ...payload,
    sessionId: sessionContext.sessionId || "N/A",
    userType: USER_TYPE,
    AgentId: sessionContext.AgentId || "",
    commissionsSOB: sessionContext.commissionsSOB || "",
  };
}

function deliver(metric, payload, attempt, startedAt) {
  const trackFn = getTrackFn();
  const sessionOk = sessionReady || Date.now() - startedAt >= READY_CEILING_MS;
  if (trackFn && sessionOk) {
    const enriched = enrich(payload);
    logTrack(metric, enriched);
    try {
      trackFn(metric, enriched);
      return;
    } catch (error) {
      console.error("GTM track failed:", error);
    }
  }

  if (sessionOk && (attempt >= MAX_ATTEMPTS - 1 || Date.now() - startedAt >= READY_CEILING_MS)) {
    return;
  }

  let delay = SESSION_POLL_MS;
  if (sessionOk) {
    delay = attempt === 0 ? 0 : RETRY_MS;
  }
  const nextAttempt = sessionOk ? attempt + 1 : attempt;
  const timer = setTimeout(() => deliver(metric, payload, nextAttempt, startedAt), delay);
  timers.push(timer);
}

function logTrack(metric, payload) {
  if (process.env.NODE_ENV !== "development") {
    return;
  }
  console.info("[GTM Tracking]", metric, payload);
}

export function emit(metric, payload, { unload = false } = {}) {
  const stamped = { ...payload };
  if (unload) {
    const enriched = enrich(stamped);
    logTrack(metric, enriched);
    const trackFn = getTrackFn();
    if (!trackFn) {
      return;
    }
    try {
      trackFn(metric, enriched);
    } catch (error) {
      console.error("GTM track failed:", error);
    }
    return;
  }
  deliver(metric, stamped, 0, Date.now());
}

export function trackPageLoad(eventMetaData) {
  emit(GtmMetric.PageView, {
    ...eventMetaData,
    eventType: GtmEventType.PageLoad,
    isDomLoad: false,
  });
}

export function trackPageExit(eventMetaData, options) {
  emit(GtmMetric.PageView, {
    ...eventMetaData,
    eventType: GtmEventType.PageExit,
  }, options);
}

export function trackButtonClick(eventMetaData) {
  emit(GtmMetric.Click, stampEvent({
    ...eventMetaData,
    eventType: GtmEventType.ButtonClick,
  }));
}

export function trackLinkClick(eventMetaData) {
  emit(GtmMetric.Click, stampEvent({
    ...eventMetaData,
    eventType: GtmEventType.LinkClick,
  }));
}

export function trackRadioChange(eventMetaData) {
  emit(GtmMetric.Click, stampEvent({
    ...eventMetaData,
    eventType: GtmEventType.RadioChange,
  }));
}

export function trackCheckboxChange(eventMetaData) {
  emit(GtmMetric.Click, stampEvent({
    ...eventMetaData,
    eventType: GtmEventType.CheckboxChange,
  }));
}

export function trackModalLoad(typeName) {
  emit(GtmMetric.ModalLoad, stampEvent({
    type: "modalLoad",
    typeName,
    pageName: visit?.pageName || "",
  }));
}

export function getLanguage() {
  const lang = (document.documentElement?.lang || "en").toLowerCase();
  if (lang.startsWith("es")) {
    return "Spanish";
  }
  if (lang.startsWith("en")) {
    return "English";
  }
  return lang;
}

export function getPageLoadLatency(isInitialLoad) {
  if (!isInitialLoad || typeof performance === "undefined") {
    return 0;
  }

  const nav = performance.getEntriesByType?.("navigation")?.[0];
  if (nav && Number.isFinite(nav.duration)) {
    return Math.round(nav.duration);
  }

  if (nav && Number.isFinite(nav.loadEventEnd) && Number.isFinite(nav.startTime)) {
    return Math.max(0, Math.round(nav.loadEventEnd - nav.startTime));
  }

  return Math.round(performance.now());
}

export function resetGtmTrackerForTests() {
  visit = null;
  lastExitEpoch = 0;
  sessionReady = false;
  sessionContext = {
    sessionId: "N/A",
    AgentId: "",
    commissionsSOB: "",
  };
  previousRadioByName.clear();
  timers.forEach((timer) => clearTimeout(timer));
  timers = [];
}
